import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Send, ArrowLeft } from 'lucide-react';
import { api, formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Watermark } from '../components/Watermark';
import { ConfidentialBanner } from '../components/ConfidentialBanner';
import { useContentProtection } from '../hooks/useContentProtection';

export default function MessagesPage() {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const protectedRef = useRef(null);
  useContentProtection(protectedRef);

  useEffect(() => {
    loadConversations();
  }, []);

  useEffect(() => {
    if (selectedConv) {
      loadMessages(selectedConv.id);
    }
  }, [selectedConv]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadConversations = async () => {
    try {
      const data = await api.getConversations();
      setConversations(data);
      if (data.length > 0 && !selectedConv) {
        setSelectedConv(data[0]);
      }
    } catch (e) {
      console.error('Error loading conversations:', e);
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (conversationId) => {
    try {
      const data = await api.getMessages(conversationId);
      setMessages(data);
    } catch (e) {
      console.error('Error loading messages:', e);
    }
  };

  const handleSend = async () => {
    if (!newMessage.trim() || !selectedConv) return;

    setSending(true);
    try {
      const otherId = selectedConv.participant_ids.find(id => id !== user.id);
      await api.sendMessage({
        receiver_id: otherId,
        listing_id: selectedConv.listing_id,
        content: newMessage
      });
      setNewMessage('');
      loadMessages(selectedConv.id);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="h-[calc(100vh-200px)] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="messages-page" ref={protectedRef}>
      <h1 className="text-2xl md:text-3xl font-light tracking-tight text-zinc-100 mb-6 font-[Outfit]">
        Mensajes
      </h1>
      <ConfidentialBanner />

      <Watermark>
      <div className="glass-card overflow-hidden h-[calc(100vh-300px)] flex" style={{ userSelect: 'none', WebkitUserSelect: 'none' }}>
        {/* Conversations List */}
        <div className={`w-full md:w-80 border-r border-white/5 flex-shrink-0 ${
          selectedConv && 'hidden md:block'
        }`}>
          <div className="p-4 border-b border-white/5">
            <h2 className="font-medium text-zinc-100">Conversaciones</h2>
          </div>
          {conversations.length > 0 ? (
            <div className="overflow-y-auto h-[calc(100%-60px)]">
              {conversations.map((conv) => (
                <button
                  key={conv.id}
                  onClick={() => setSelectedConv(conv)}
                  className={`w-full p-4 text-left hover:bg-white/5 transition-colors ${
                    selectedConv?.id === conv.id ? 'bg-white/5' : ''
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center flex-shrink-0">
                      {conv.other_user?.name?.charAt(0).toUpperCase() || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-zinc-100 truncate">
                          {conv.other_user?.name || 'Usuario'}
                        </p>
                        {conv.unread_count > 0 && (
                          <span className="px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400 text-xs">
                            {conv.unread_count}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-zinc-500 truncate">{conv.last_message}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <p className="text-zinc-500">No tienes conversaciones</p>
            </div>
          )}
        </div>

        {/* Messages Area */}
        {selectedConv ? (
          <div className="flex-1 flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-white/5 flex items-center gap-3">
              <button 
                onClick={() => setSelectedConv(null)}
                className="md:hidden p-2 -ml-2 text-zinc-400"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center">
                {selectedConv.other_user?.name?.charAt(0).toUpperCase() || '?'}
              </div>
              <div>
                <p className="font-medium text-zinc-100">{selectedConv.other_user?.name || 'Usuario'}</p>
                {selectedConv.listing_title && (
                  <p className="text-xs text-zinc-500">{selectedConv.listing_title}</p>
                )}
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((msg) => {
                const isMine = msg.sender_id === user.id;
                return (
                  <div 
                    key={msg.id}
                    className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                      isMine 
                        ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white' 
                        : 'bg-white/5 text-zinc-100'
                    }`}>
                      <p className="text-sm">{msg.content}</p>
                      <p className={`text-xs mt-1 ${isMine ? 'text-white/60' : 'text-zinc-500'}`}>
                        {new Date(msg.created_at).toLocaleTimeString('es-MX', { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                      </p>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 border-t border-white/5">
              <div className="flex gap-2">
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
                  placeholder="Escribe un mensaje..."
                  className="bg-white/5 border-white/10 text-zinc-100"
                  data-testid="message-input"
                />
                <Button 
                  onClick={handleSend}
                  disabled={sending || !newMessage.trim()}
                  className="bg-purple-600 hover:bg-purple-500"
                  data-testid="send-message-btn"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="hidden md:flex flex-1 items-center justify-center">
            <p className="text-zinc-500">Selecciona una conversacion</p>
          </div>
        )}
      </div>
      </Watermark>
    </div>
  );
}
